import { END_USER_CONNECT_READY_STATES } from './endUserJourneyState.data';

type EndUserAccountState = string | null | undefined;
type EndUserProfileState = string | null | undefined;

export type EndUserSideMenuKey = 'home' | 'hsa-paydays' | 'expenses' | 'my-account';

export const normalizeEndUserAccountState = (accountState: EndUserAccountState): string | null => {
  const normalized = accountState?.trim().toLowerCase();
  return normalized || null;
};

export const normalizeEndUserProfileState = (profileState: EndUserProfileState): string | null => {
  const normalized = profileState?.trim().toLowerCase();
  return normalized || null;
};

/** Aligns `profile_enhanced` / `tcb_calculated` with hyphenated journey constants. */
export const normalizeEndUserConnectReadyState = (
  profileState: EndUserProfileState
): string | null => {
  const normalized = normalizeEndUserProfileState(profileState)?.replace(/_/g, '-') || null;
  return normalized || null;
};

/** Profile enhanced or later (e.g. `tcb_calculated`) — eligible for Connect / Retry CTAs. */
export const isEndUserConnectReadyProfileState = (profileState: EndUserProfileState): boolean => {
  const normalized = normalizeEndUserConnectReadyState(profileState);
  return normalized != null && END_USER_CONNECT_READY_STATES.has(normalized);
};

export const isEndUserAccountConnected = (accountState: EndUserAccountState): boolean =>
  normalizeEndUserAccountState(accountState) === 'connected';

/** Carrier-rejected credentials (`invalid`) or enrollment failure (`failed`). */
export const isEndUserAccountConnectionError = (accountState: EndUserAccountState): boolean => {
  const normalized = normalizeEndUserAccountState(accountState);
  return normalized === 'invalid' || normalized === 'failed';
};

export const isEndUserConnectionBlocked = (accountState: EndUserAccountState): boolean => {
  const normalized = normalizeEndUserAccountState(accountState);
  return normalized === 'pending' || normalized === 'failed';
};

/** Retry CTA on the dashboard stepper when connection is in an error state. */
export const canEnableEndUserConnectRetry = (
  accountState: EndUserAccountState,
  profileState: EndUserProfileState
): boolean =>
  isEndUserAccountConnectionError(accountState) && isEndUserConnectReadyProfileState(profileState);

/** HSA paydays, expenses, confirm-cashback, and related flows. */
export const canAccessEndUserFullFlows = (
  accountState: EndUserAccountState,
  hasExpenses: boolean
): boolean => isEndUserAccountConnected(accountState) && hasExpenses;

/** TPA / find-expenses connection flow (not available while pending or failed). */
export const canAccessEndUserTpaConnectionFlow = (accountState: EndUserAccountState): boolean =>
  !isEndUserConnectionBlocked(accountState);

/**
 * Home stepper Connect is interactive after profile enhancement (or TCB calculated)
 * and before the health account is connected (and while TPA connection is allowed).
 */
export const canEnableEndUserConnectAccount = (
  accountState: EndUserAccountState,
  profileState: EndUserProfileState
): boolean =>
  !isEndUserAccountConnectionError(accountState) &&
  canAccessEndUserTpaConnectionFlow(accountState) &&
  !isEndUserAccountConnected(accountState) &&
  isEndUserConnectReadyProfileState(profileState);

/**
 * Until connected + has expenses, keep Home (connect stepper) and My Account
 * interactive; lock HSA Paydays and Expenses.
 */
export const getDisabledEndUserSideMenuKeys = (
  accountState: EndUserAccountState,
  hasExpenses: boolean
): EndUserSideMenuKey[] => {
  if (canAccessEndUserFullFlows(accountState, hasExpenses)) {
    return [];
  }

  return ['hsa-paydays', 'expenses'];
};
